<template>
    <div
        v-show="enabled"
        ref="menuAnchorRef"
        :style="{ position: 'fixed', left: `${menuX}px`, top: `${menuY}px`, width: '1px', height: '1px', pointerEvents: 'none' }"
    />
    <QMenu
        v-if="menuAnchorRef"
        v-model="menuVisible"
        no-parent-event
        no-focus
        no-refocus
        :target="menuAnchorRef"
        anchor="top left"
        self="top left"
        :offset="[0, 4]"
    >
        <QCard style="border: 1px solid silver; border-radius: 8px; width: 240px;" class="q-pa-xs">
            <QInput
                ref="quickSearchRef"
                placeholder="Search"
                v-model="quickSearch"
                outlined
                dense
                @keydown="onQuickSearchKeydown"
                hide-bottom-space
            />
            <QList dense>
                <QItem v-if="quicktextLoading" dense>
                    <QItemSection avatar>
                        <QBtn icon="mdi-loading" class="mdi-animate-spin" flat dense size="sm" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>Loading quick text...</QItemLabel>
                    </QItemSection>
                </QItem>
                <QItem v-else-if="quickTextError" dense>
                    <QItemSection avatar>
                        <QBtn icon="mdi-alert-circle-outline" flat dense size="sm" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>{{ quickTextError }}</QItemLabel>
                    </QItemSection>
                </QItem>
                <QItem v-else-if="filteredQuickText.length === 0" dense>
                    <QItemSection avatar>
                        <QBtn icon="mdi-magnify-close" flat dense size="sm" />
                    </QItemSection>
                    <QItemSection>
                        <QItemLabel>No quick-text matches</QItemLabel>
                    </QItemSection>
                </QItem>
                <template v-else>
                    <QItem
                        v-for="(item, index) in filteredQuickText"
                        :key="index"
                        clickable
                        :active="selectedQuickTextIdx === index"
                        active-class="bg-grey-3 text-dark"
                        @click="selectMenuItem(item)"
                    >
                        <QItemSection avatar>
                            <QBtn :icon="item.icon || 'mdi-text'" flat dense size="sm" />
                        </QItemSection>
                        <QItemSection>
                            <QItemLabel>{{ item.title }}</QItemLabel>
                        </QItemSection>
                    </QItem>
                </template>
            </QList>
        </QCard>
    </QMenu>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { QMenu, QCard, QInput, QList, QItem, QItemSection, QItemLabel, QBtn } from 'quasar';
import api from '@/Services/api';
import utils from '@/Shared/utils.jsx';
import { loadQuickTextList, getItemInsertPayload } from './quickTextKit.js';
import { menuCoordsBelowPos } from './useTipTapQuickTextHelpers.js';

const props = defineProps({
    editor: { type: Object, default: null },
    enabled: { type: Boolean, default: true },
});

/** TipTap throws when accessing `editor.view.dom` if the view was destroyed or not mounted. */
function getEditorDom(editor) {
    if (!editor || editor.isDestroyed)
        return null;
    try {
        return editor.view?.dom ?? null;
    }
    catch {
        return null;
    }
}

const menuAnchorRef = ref(null);
const menuVisible = ref(false);
const menuX = ref(0);
const menuY = ref(0);
const quickSearch = ref('');
const quicktextLoading = ref(false);
const selectedQuickTextIdx = ref(0);
const quickTextError = ref('');
const menuItems = ref([]);
const quickTextAwaitingIntent = ref(false);
const quickTextReplaceRange = ref(null);
const quickSearchRef = ref(null);

const filteredQuickText = computed(() => {
    if (quickSearch.value) {
        const q = quickSearch.value.toLowerCase();
        return menuItems.value.filter((i) =>
            i.title.toLowerCase().includes(q)
            || i.tags.some((a) => a.toLowerCase().includes(q)),
        );
    }
    return menuItems.value;
});

watch(quickSearch, () => {
    selectedQuickTextIdx.value = 0;
});

watch(menuVisible, (v) => {
    const dom = getEditorDom(props.editor);
    if (!dom)
        return;
    if (v) {
        dom.addEventListener('keydown', onEditorKeydownWhileMenu, true);
        dom.addEventListener('keydown', onEditorIntentKeydown, true);
    }
    else {
        dom.removeEventListener('keydown', onEditorKeydownWhileMenu, true);
        dom.removeEventListener('keydown', onEditorIntentKeydown, true);
    }
});

function focusQuickSearch() {
    quickTextAwaitingIntent.value = false;
    const c = quickSearchRef.value;
    if (c?.focus) {
        c.focus();
        return;
    }
    c?.$el?.querySelector?.('input')?.focus?.();
}

function onEditorKeydownWhileMenu(event) {
    if (!menuVisible.value)
        return;
    if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu(true);
        return;
    }
    if (event.key === 'ArrowDown' && quickTextAwaitingIntent.value) {
        event.preventDefault();
        window.setTimeout(() => focusQuickSearch(), 0);
    }
}

function isTypingKey(key) {
    return key.length === 1 || key === 'Backspace' || key === 'Delete' || key === ' ';
}

function onEditorIntentKeydown(event) {
    if (!menuVisible.value || !quickTextAwaitingIntent.value)
        return;
    if (event.key === 'ArrowDown' || event.key === 'Escape')
        return;
    const popupFocused = quickSearchRef.value?.$el?.contains?.(document.activeElement);
    if (popupFocused)
        return;
    if (isTypingKey(event.key))
        closeMenu(false);
}

let domHashtag = null;

function attachDomListeners(ed) {
    detachDomListeners();
    const dom = getEditorDom(ed);
    if (!dom || !props.enabled)
        return;
    domHashtag = (event) => {
        if (!props.enabled || !props.editor)
            return;
        if (event.shiftKey && (event.key === '#' || event.code === 'Digit3')) {
            event.preventDefault();
            event.stopPropagation();
            openFromHashtag();
        }
    };
    dom.addEventListener('keydown', domHashtag, true);
}

function detachDomListeners() {
    const dom = getEditorDom(props.editor);
    if (dom && domHashtag)
        dom.removeEventListener('keydown', domHashtag, true);
    domHashtag = null;
}

watch(
    () => props.editor,
    (ed) => {
        detachDomListeners();
        if (ed && props.enabled)
            attachDomListeners(ed);
    },
);

watch(
    () => props.enabled,
    (en) => {
        if (en && props.editor)
            attachDomListeners(props.editor);
        else
            detachDomListeners();
    },
);

onMounted(() => {
    if (props.editor && props.enabled)
        attachDomListeners(props.editor);
});

onBeforeUnmount(() => {
    detachDomListeners();
    const dom = getEditorDom(props.editor);
    if (dom) {
        dom.removeEventListener('keydown', onEditorKeydownWhileMenu, true);
        dom.removeEventListener('keydown', onEditorIntentKeydown, true);
    }
});

function openFromHashtag() {
    const ed = props.editor;
    if (!getEditorDom(ed))
        return;

    const from = ed.state.selection.from;
    ed.chain().focus().insertContentAt(from, '#').run();

    const afterHash = from + 1;
    quickTextReplaceRange.value = { from, to: afterHash };

    const { x, y } = menuCoordsBelowPos(ed, afterHash);
    menuX.value = x;
    menuY.value = y;
    quickSearch.value = '';
    selectedQuickTextIdx.value = 0;
    quickTextError.value = '';
    quicktextLoading.value = true;
    quickTextAwaitingIntent.value = true;
    menuVisible.value = true;

    loadQuickTextList(api, utils)
        .then((rows) => {
            menuItems.value = rows;
        })
        .catch((e) => {
            menuItems.value = [];
            quickTextError.value = 'Could not load quick-text entries';
            utils.warn('QuickText could not load: ' + e);
        })
        .finally(() => {
            quicktextLoading.value = false;
        });

    nextTick(() => {
        if (getEditorDom(props.editor))
            props.editor.chain().focus().setTextSelection(afterHash).run();
    });
}

function closeMenu(focusEditor = true) {
    menuVisible.value = false;
    quickTextAwaitingIntent.value = false;
    quickTextReplaceRange.value = null;
    if (focusEditor) {
        nextTick(() => {
            if (getEditorDom(props.editor))
                props.editor.chain().focus().run();
        });
    }
}

function selectMenuItem(item) {
    const ed = props.editor;
    if (!getEditorDom(ed))
        return;

    const payload = getItemInsertPayload(item);
    const richHtml = payload.richHtml || '';
    const plainText = payload.plainText || '';
    if (!richHtml && !plainText)
        return;

    const range = quickTextReplaceRange.value;
    if (!range || typeof range.from !== 'number' || typeof range.to !== 'number')
        return;

    ed.chain().focus().deleteRange({ from: range.from, to: range.to }).run();

    if (richHtml)
        ed.chain().focus().insertContentAt(range.from, richHtml, { contentType: 'html' }).run();
    else
        ed.chain().focus().insertContentAt(range.from, plainText).run();

    quickTextReplaceRange.value = null;
    closeMenu(true);
}

function onQuickSearchKeydown(e) {
    switch (e.key) {
        case 'Enter':
            e.preventDefault();
            {
                const item = filteredQuickText.value[selectedQuickTextIdx.value];
                if (item)
                    selectMenuItem(item);
            }
            break;
        case 'ArrowDown':
            e.preventDefault();
            if (!filteredQuickText.value.length)
                break;
            selectedQuickTextIdx.value++;
            if (selectedQuickTextIdx.value >= filteredQuickText.value.length)
                selectedQuickTextIdx.value = 0;
            break;
        case 'ArrowUp':
            e.preventDefault();
            if (!filteredQuickText.value.length)
                break;
            selectedQuickTextIdx.value--;
            if (selectedQuickTextIdx.value < 0)
                selectedQuickTextIdx.value = filteredQuickText.value.length - 1;
            break;
        case 'Escape':
            e.preventDefault();
            closeMenu(true);
            break;
        default:
            break;
    }
}

defineExpose({ openFromHashtag, closeMenu, menuVisible });
</script>
